import type { Ref } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Icon } from './Icon';

export type ConfirmationTone = 'success' | 'error' | 'warning' | 'info';

const DEFAULT_ICON_NAME: Record<ConfirmationTone, string> = {
  success: 'i-check',
  error: 'i-x',
  warning: 'i-bolt',
  info: 'i-chat',
};

export interface ConfirmationProps extends Omit<HTMLAttributes<HTMLElement>, 'title' | 'children'> {
  tone: ConfirmationTone;
  icon?: ReactNode;
  title: ReactNode;
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  inline?: boolean;
  children?: ReactNode;
}

export function Confirmation(
  { ref, tone, icon, title, headingLevel = 'h3', inline = false, children, className, ...rest }: ConfirmationProps & { ref?: Ref<HTMLElement> },
) {
  const Heading = headingLevel;
  return (
    <article ref={ref} className={cx('confirmation', inline && 'confirmation--inline', className)} {...rest}>
      <div className={cx('confirmation-icon', `is-${tone}`)}>
        {icon ?? (
          <Icon
            name={DEFAULT_ICON_NAME[tone]}
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </div>
      <Heading className="confirmation-header">{title}</Heading>
      {children != null && <p className="confirmation-footer">{children}</p>}
    </article>
  );
}
